/**
 * Helpers that keep browser telemetry free of secrets and personal data.
 *
 * Telemetry code builds its log fields from an allowlist and only runs
 * free-form values (error texts, header values, URLs) through these helpers.
 */

const FINGERPRINT_LENGTH = 8;
const SALT_BYTE_LENGTH = 16;
const HEX_RADIX = 16;
const HEX_BYTE_WIDTH = 2;
const DEFAULT_TEXT_LIMIT = 120;
const DEFAULT_HEADER_LIMIT = 128;

export const REDACTED = "[redacted]";
const FINGERPRINT_UNAVAILABLE = "n/a";

const HTML_TAG_PATTERN = /<[^>]*>/g;
const WHITESPACE_PATTERN = /\s+/g;
const EMAIL_PATTERN = /[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+/g;
// Separate, non-global copy: `.test()` on a global regex keeps state between calls.
const EMAIL_TEST_PATTERN = /[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+/;
const LONG_DIGIT_RUN_PATTERN = /\d{6,}/g;
const TOKEN_LIKE_PATTERN = /[A-Za-z0-9_-]{24,}/g;
const PATH_PARAMETER_PATTERN = /;[^/?#]*/g;
const SENSITIVE_HEADER_VALUE_PATTERN =
  /bearer\s|jsessionid|session|cookie|authorization|password|token|[\w-]+=\S{8,}/i;

let sessionSalt: string | null = null;

/**
 * Random per page session and kept in memory only. Fingerprints are therefore
 * comparable inside one session, but not across sessions or users.
 */
function getSessionSalt(): string {
  if (sessionSalt) {
    return sessionSalt;
  }

  const bytes = new Uint8Array(SALT_BYTE_LENGTH);
  crypto.getRandomValues(bytes);
  sessionSalt = Array.from(bytes, (byte) =>
    byte.toString(HEX_RADIX).padStart(HEX_BYTE_WIDTH, "0")
  ).join("");

  return sessionSalt;
}

/** Test hook: forget the salt so the next fingerprint starts a new "session". */
export function resetFingerprintSalt(): void {
  sessionSalt = null;
}

/**
 * Salted SHA-256 prefix of a secret value, e.g. the CSRF value of the cart
 * page. Lets two requests be compared without exposing the value itself.
 */
export async function fingerprint(
  value: string | null | undefined
): Promise<string> {
  if (!value) {
    return FINGERPRINT_UNAVAILABLE;
  }

  try {
    const subtle = globalThis.crypto?.subtle;

    if (!subtle) {
      return FINGERPRINT_UNAVAILABLE;
    }

    const digest = await subtle.digest(
      "SHA-256",
      new TextEncoder().encode(`${getSessionSalt()}:${value}`)
    );

    return Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(HEX_RADIX).padStart(HEX_BYTE_WIDTH, "0")
    )
      .join("")
      .slice(0, FINGERPRINT_LENGTH);
  } catch {
    return FINGERPRINT_UNAVAILABLE;
  }
}

/**
 * Free text (e.g. an accelerator error message): strips markup, masks e-mail
 * addresses, long digit runs and token-like strings, then truncates.
 */
export function sanitizeText(
  text: string | null | undefined,
  maxLength = DEFAULT_TEXT_LIMIT
): string {
  if (!text) {
    return "";
  }

  return text
    .replace(HTML_TAG_PATTERN, " ")
    .replace(WHITESPACE_PATTERN, " ")
    .trim()
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(LONG_DIGIT_RUN_PATTERN, REDACTED)
    .replace(TOKEN_LIKE_PATTERN, REDACTED)
    .slice(0, maxLength);
}

/**
 * Value of an allowlisted response header. Keeps identifiers such as request
 * IDs intact and drops anything that looks like a credential or session value.
 */
export function sanitizeHeaderValue(
  value: string | null | undefined,
  maxLength = DEFAULT_HEADER_LIMIT
): string | undefined {
  const trimmed = value?.trim();

  if (!trimmed) {
    return;
  }

  if (
    SENSITIVE_HEADER_VALUE_PATTERN.test(trimmed) ||
    EMAIL_TEST_PATTERN.test(trimmed)
  ) {
    return REDACTED;
  }

  return trimmed.slice(0, maxLength);
}

/**
 * Pathname of a URL without host, query, fragment or path parameters (servlet
 * containers can put a `;jsessionid=...` there). Null when it cannot be parsed.
 */
export function safePath(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    const { pathname } = new URL(url, "http://localhost");
    return pathname.replace(PATH_PARAMETER_PATTERN, "");
  } catch {
    return null;
  }
}
